import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import Seo from "../components/Seo";
import NotFoundPage from "./NotFoundPage";
import {
  GiB, GPUS, MODELS, RUNTIMES, WEIGHT_FORMATS, compute, modelSheet, usableMemGiB, SHEET_CONTEXT, gpusNeeded, offersFor, isNewModel, formatOk, defaultEngine, sheetLabel,
  decodeSpeed, weightsPerGpu, smallestMac, LARGEST_MAC,
  type GpuPrices, type ModelPreset, type ModelSheet,
} from "./llmVram";
import { countryName } from "./labs";
import Flag from "../components/Flag";
import { VRAM_MODEL_SEO, modelBySlug, modelName, modelPath, modelSlug } from "../seo/vramModels";
import { SITE } from "../seo/routes";
import { badgeValue, badgeWidth } from "../seo/publicApi";
import { HOURS_PER_MONTH, PRICED_GPUS, gpuPricePath, gpuShort, usd } from "../seo/gpuPrices";
import { rentRel, rentUrl } from "../data/referrals";
import { usePrices, useLiveData } from "./usePrices";
import { CATALOG_SNAPSHOT, compact, labOf, licenseInfo, ollamaCommand, runsWith, strataFastest, strataHow, strataWrite, validateCatalog, type CatalogModel } from "./catalog";
import SiteFooter from "../components/SiteFooter";
import { PRICE_SOURCE_LIST } from "../seo/priceSources";
import { SpareCoresCredit } from "./SpareCoresCredit";
import "./llm-vram.css";

// Une page par modèle : « combien de VRAM pour X ? ». Tout le contenu est calculé par
// le moteur du calculateur (llmVram.ts), rien n'est écrit à la main par modèle :
// les chiffres ne peuvent pas diverger de ceux du calculateur.

const gib = (x: number) => (x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x.toFixed(2));
const gb = (x: number) => (x * 1.073741824).toFixed(x >= 10 ? 0 : 1); // GiB → GB (10⁹ octets)
const tokens = (n: number) => (n >= 1 << 20 ? `${n / (1 << 20)}M` : `${n / 1024}K`);
/** « an 8K prompt », « a 32K prompt » : l'article suit la prononciation du nombre. */
const aOrAn = (label: string) => `${/^(8|11|18)/.test(label) ? "an" : "a"} ${label}`;
// Fuseau fixé : le HTML prérendu et le navigateur écrivent la même date.
const longDate = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });
const monthYear = (day: string) =>
  new Date(`${day.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", year: "numeric" });

// ── Ce que le modèle sait faire (catalogue, champ about : scripts/sources/modelcard.ts) ──
type About = NonNullable<CatalogModel["about"]>;
const INPUTS: Record<string, string> = { text: "text", image: "images", video: "video", audio: "audio" };
/** « text, images and video » */
const andList = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const readsText = (a: About) => andList((a.inputs ?? ["text"]).map((x) => INPUTS[x] ?? x));
const capital = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
function reasoningText(a: About): string | undefined {
  switch (a.thinking) {
    case "toggle": return "Thinking mode you can switch on or off";
    case "effort": return a.efforts ? `Adjustable reasoning effort (${a.efforts.join(", ")})` : "Adjustable reasoning effort";
    case "always": return "Always reasons before it answers";
    case "supported": return "Has a reasoning mode";
    default: return undefined;
  }
}
const languageName = (code: string) => {
  try { return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code; } catch { return code; }
};
/** 7,08 × 10²⁴ : mantisse et exposant, l'exposant en exposant HTML. */
const sci = (x: number) => { const e = Math.floor(Math.log10(x)); return { m: (x / 10 ** e).toFixed(1), e }; };
const bigTokens = (x: number) => (x >= 1e12 ? `${(x / 1e12).toFixed(x >= 1e13 ? 0 : 1)} trillion` : `${Math.round(x / 1e9)} billion`);
type Sampling = NonNullable<About["sampling"]>;
/** Réglages à afficher ; une température quasi nulle est un décodage glouton (Qwen2.5-VL : 0,000001). */
const samplingRows = (sp: Sampling): [string, string, string][] => ([
  ["Temperature", sp.temperature === undefined ? undefined : sp.temperature < 0.05 ? "0 (greedy: same answer every time)" : String(sp.temperature), sp.temperature === undefined ? "" : `--temp ${sp.temperature < 0.05 ? 0 : sp.temperature}`],
  ["Top-p", sp.topP === undefined ? undefined : String(sp.topP), `--top-p ${sp.topP}`],
  ["Top-k", sp.topK === undefined ? undefined : String(sp.topK), `--top-k ${sp.topK}`],
  ["Min-p", sp.minP === undefined ? undefined : String(sp.minP), `--min-p ${sp.minP}`],
  ["Repetition penalty", sp.repetitionPenalty === undefined ? undefined : String(sp.repetitionPenalty), `--repeat-penalty ${sp.repetitionPenalty}`],
] as [string, string | undefined, string][]).filter((r): r is [string, string, string] => r[1] !== undefined);
const samplingSummary = (sp: Sampling) => samplingRows(sp).map(([k, v]) => `${k.toLowerCase()} ${v.split(" (")[0]}`).join(", ");
/** Case du tableau : au-delà de 8 GPU, plusieurs serveurs (GPU de serveur) ou plusieurs machines (les autres). */
/** Quantifications GGUF les plus téléchargées, toujours visibles ; la plus petite l'est aussi. */
const ggufRow = ({ quant, bytes, card }: { quant: string; bytes: number; card: ReturnType<typeof smallestCard> }) => (
  <tr key={quant}>
    <th scope="row">{quant}</th>
    <td>{gib(bytes / GiB)} GiB</td>
    <td className={card ? "vram-table-ok" : undefined}>{card ? `${card.label.split(" · ")[0].replace(/^GeForce /, "")} (${card.vram} GB)` : "More than 96 GB: see the calculator"}</td>
  </tr>
);
const GGUF_KEY = /^(BF16|F16|Q8_0|Q6_K|Q5_K_M|Q4_K_M|IQ4_XS|Q3_K_M|Q2_K|MXFP4(_MOE)?)$/;
const count = (n: number | null, server = true) => (n === null ? "> 128" : n > 8 ? `${n} (several ${server ? "servers" : "machines"})` : String(n));
/** Dans une phrase : le nombre seul. */
const plain = (n: number | null) => (n === null ? "more than 128" : String(n));

/** Échelle des cartes grand public, de la plus petite mémoire à la plus grande. */
const LOCAL_LADDER = ["rtx-4060", "rtx-4070", "rtx-4080", "rtx-4090", "rtx-5090", "rtx-6000-ada", "rtx-pro-6000"]
  .map((id) => GPUS.find((g) => g.id === id)!);
const LLAMA = RUNTIMES.find((r) => r.id === "llamacpp")!;

/** Plus petite carte où un fichier GGUF tient avec une requête de SHEET_CONTEXT tokens (llama.cpp). */
function smallestCard(m: ModelPreset, bytes: number) {
  return LOCAL_LADDER.find((g) => compute({
    model: m, mode: "inference", weightBits: 4.85, exactWeightBytes: bytes, layerSplit: true, kvBytes: 2,
    context: SHEET_CONTEXT, sequences: 1, tp: 1, pp: 1, vramGiB: usableMemGiB(g), utilization: LLAMA.utilization,
    overheadGiB: LLAMA.overheadGiB, target: { engine: "llamacpp", gpu: g }, gradientCheckpointing: true, shardStates: true, loraFraction: 0.01,
  }).status !== "oom");
}

/** Badge à coller dans une fiche Hugging Face ou un README, avec un bouton pour le copier. */
function BadgeSnippet({ name, slug, path, width }: { name: string; slug: string; path: string; width: number }) {
  const [copied, setCopied] = useState(false);
  const md = `[![${name} VRAM](${SITE}/badge/${slug}.svg)](${SITE}${path})`;
  return (
    <>
      {/* Largeur réservée (celle du SVG) : sans elle, l'image arrivée décalait la suite de la page. */}
      <p><img src={`/badge/${slug}.svg`} alt={`${name} VRAM badge`} width={width} height={20} /></p>
      <div className="vram-code-wrap">
        <pre className="vram-code" tabIndex={0} aria-label="Badge Markdown"><code>{md}</code></pre>
        <button type="button" className="vram-share vram-code-copy" onClick={() => {
          navigator.clipboard.writeText(md).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }, () => {});
        }}>{copied ? "Copied" : "Copy"}</button>
      </div>
    </>
  );
}

/** Pourquoi le cache KV a cette taille, déduit de la structure du cache (pas du nom du modèle). */
function kvNote(m: ModelPreset, saving: number) {
  // Aucune couche d'attention (Mamba-Codestral) : pas de cache KV, un état fixe par requête.
  if (!m.kv.length) return `It has no attention layers, so no KV cache: each request holds a fixed ${gib((m.stateBytesPerSeq ?? 0) / 2 ** 20)} MiB state, whatever the context.`;
  const kvLayers = Math.max(...m.kv.map((g) => g.layers));
  let why: string;
  if (m.kv.some((g) => g.ratio)) why = "it keeps only a short window of tokens at full detail and compresses the rest of the sequence";
  else if (m.stateBytesPerSeq && kvLayers < m.layers) why = `only ${kvLayers} of its ${m.layers} layers keep a KV cache; the others hold a small fixed-size state`;
  else if (m.kv.some((g) => g.window)) why = "most layers only keep a short window of recent tokens";
  else if (m.kv.some((g) => !g.tpHeads)) why = "MLA stores one compressed latent per token and layer instead of full keys and values";
  else return "Every layer keeps keys and values for every token (grouped-query attention), so the cache grows linearly with context.";
  return saving > 1.5
    ? `Its KV cache is ${saving.toFixed(1)}× smaller than if every layer used full attention, because ${why}.`
    : `Its cache stays compact because ${why}.`;
}

/** Pour chaque GPU loué : le moins de cartes qui suffisent (un serveur, 8 au plus) et, à ce
 *  nombre, le format de poids le plus fidèle (checkpoint officiel, puis BF16, FP8, 4-bit),
 *  au prix du fournisseur le moins cher. Triées du moins cher au plus cher. Seulement des formats
 *  que le GPU calcule nativement : pas de checkpoint FP8 sur un A100, et INT8 plutôt que FP8. */
function cheapestSetups(m: ModelPreset, { formats }: ModelSheet, prices: GpuPrices) {
  return PRICED_GPUS.flatMap((g) => {
    const offer = offersFor(prices, g.id)[0];
    if (!offer) return [];
    const engine = defaultEngine(g); // comme gpusNeeded
    const fits = formats.map((f) => ({ f, n: gpusNeeded(m, g, f.bits) }))
      .filter((x): x is { f: typeof x.f; n: number } => x.n !== null && x.n <= 8 && (x.f.id !== "native" || formatOk(g, engine, m, "native")));
    if (!fits.length) return [];
    const n = Math.min(...fits.map((x) => x.n));
    const f = fits.find((x) => x.n === n)!.f; // formats rangés du plus fidèle au plus compressé
    const label = sheetLabel(f.id, engine, g, f.label); // « 8-bit » : FP8, INT8 ou Q8_0 selon le GPU et le moteur
    return [{ g, n, f, label, offer, hourly: n * offer.price }];
  }).sort((a, b) => a.hourly - b.hourly);
}

type Setup = ReturnType<typeof cheapestSetups>[number];
/** « GeForce RTX 3090 (24 GB) », ou « NVIDIA A100 80GB » quand le nom porte déjà la mémoire. */
const gpuWithMem = (g: Setup["g"]) => (gpuShort(g).endsWith("GB") ? gpuShort(g) : `${gpuShort(g)} (${g.vram} GB)`);
const setupText = (x: Setup) => `${x.n} × ${gpuWithMem(x.g)} with ${x.label.replace(" (native)", "")} weights on ${x.offer.name.split(" (")[0]}, at ${usd(x.hourly)} per hour`;

function faqFor(m: ModelPreset, s: ModelSheet, setups: Setup[], date: string, strata?: CatalogModel["strata"], about?: About) {
  const name = modelName(m);
  const f = (id: string) => s.formats.find((x) => x.id === id)!;
  const kv = (ctx: number) => s.kv.find((k) => k.ctx === ctx)?.bf16GiB;
  const gpu = (id: string) => s.gpus.find((g) => g.id === id)!;
  const idx = (id: string) => s.formats.findIndex((x) => x.id === id);

  const rtx = gpu("rtx-4090");
  const fitsIdx = rtx.counts.findIndex((n, i) => n === 1 && rtx.supported[i]); // checkpoint FP4 : pas sur une RTX 4090
  const q4i = idx("q4_k_m");
  const single = s.gpus.find((g) => g.counts[q4i] === 1);
  const h100 = gpu("h100");
  const r5 = gpu("rtx-5090"), spark = gpu("dgx-spark"), halo = gpu("strix-halo");
  /** Format le plus fidèle qui tient sur une seule machine de cette ligne, avec la mémoire prise. */
  const oneBox = (row: ModelSheet["gpus"][number]) => {
    const i = row.counts.findIndex((n, k) => n === 1 && row.supported[k]);
    return i < 0 ? null : `${row.labels[i]} (${gib(s.formats[i].withContextGiB[0].gib)} GiB with an 8K-token context)`;
  };
  const r5Fit = oneBox(r5), sparkFit = oneBox(spark), haloFit = oneBox(halo);
  const haloName = "a Ryzen AI Max+ 395 with 128 GB, whose GPU gets up to 96 GB";
  // Mac : la mémoire totale (celle qu'on achète) est dans le libellé, la part du GPU dans vram.
  const mac4 = smallestMac(m, f("q4_k_m").bits), mac8 = smallestMac(m, f("fp8").bits);
  const ram = (g: { label: string }) => g.label.match(/(\d+) GB ·/)![1];

  return [
    {
      q: `How much VRAM does ${name} need?`,
      a: `In BF16 the weights alone take ${gib(f("bf16").weightsGiB)} GiB (${gb(f("bf16").weightsGiB)} GB). In FP8 that is ${gib(f("fp8").weightsGiB)} GiB, and about ${gib(f("q4_k_m").weightsGiB)} GiB with 4-bit quantization (Q4_K_M)` +
        (m.native ? `; the official ${m.native.label.replace(" (native)", "")} checkpoint ${m.preview ? "should take about" : "is"} ${gib(f("native").weightsGiB)} GiB` : "") +
        (m.kv.length
          ? `. Each request then adds KV cache: ${gib(kv(8192) ?? 0)} GiB at 8K tokens` + (kv(32768) !== undefined ? ` and ${gib(kv(32768)!)} GiB at 32K` : "") + ` (BF16 cache).`
          : `. It has no KV cache: each request adds a fixed ${gib((m.stateBytesPerSeq ?? 0) / GiB)} GiB state, whatever the context length.`),
    },
    {
      q: `Can ${name} run on a single RTX 4090 (24 GB)?`,
      a: fitsIdx >= 0
        ? `Yes. In ${rtx.labels[fitsIdx]} it fits on one RTX 4090 with an 8K-token context (${gib(s.formats[fitsIdx].withContextGiB[0].gib)} GiB including the cache, with llama.cpp).`
        : `No. Even in 4-bit it needs ${plain(rtx.counts[q4i])} RTX 4090s${(rtx.counts[q4i] ?? 99) > 8 ? ", spread over several machines" : ""}.` +
          (single ? ` In 4-bit it fits on a single ${single.label} (${single.vram} GB).`
            : ` In 4-bit it needs ${plain(h100.counts[q4i])} H100 GPUs${(h100.counts[q4i] ?? 99) > 8 ? ", across several servers" : ""}.`) +
          (strata ? " It does run on one RTX 4090 with Strata, an engine for gaming PCs (see below)." : ""),
    },
    {
      q: `Can ${name} run on an RTX 5090 (32 GB)?`,
      a: r5Fit
        ? `Yes. In ${r5Fit} it fits on one RTX 5090, with llama.cpp.`
        : `No. Even in 4-bit it needs ${plain(r5.counts[q4i])} RTX 5090s${(r5.counts[q4i] ?? 99) > 8 ? ", spread over several machines" : ""}.`,
    },
    {
      q: `Can ${name} run on a Mac?`,
      a: mac4
        ? `Yes. In 4-bit (Q4_K_M with llama.cpp) it needs a Mac with ${ram(mac4)} GB of unified memory${mac4.vram < LARGEST_MAC.vram ? " or more" : ""}, of which macOS gives the GPU about ${Math.round(mac4.vram)} GB, for one request with an 8K-token context.` +
          (mac8 ? (ram(mac8) === ram(mac4) ? " The same Mac also holds it in 8-bit (Q8_0)." : ` In 8-bit (Q8_0) it needs ${ram(mac8)} GB${mac8.vram < LARGEST_MAC.vram ? " or more" : ""}.`) : " In 8-bit it does not fit on a single Mac.")
        : `No single Mac holds it, even in 4-bit: it needs ${gib(f("q4_k_m").withContextGiB[0].gib)} GiB with an 8K-token context, and the largest Mac (${ram(LARGEST_MAC)} GB) gives its GPU about ${Math.round(LARGEST_MAC.vram)} GB.`,
    },
    {
      q: `Can ${name} run on a DGX Spark or a Ryzen AI Max+ 395 (128 GB)?`,
      a: sparkFit && sparkFit === haloFit
        ? `Yes. In ${sparkFit} it fits on a DGX Spark (128 GB) and on ${haloName}, with llama.cpp and one request.`
        : sparkFit
        ? `Yes. On a DGX Spark (128 GB) it fits in ${sparkFit}; on ${haloName}, ` +
          `${haloFit ? `it fits in ${haloFit}` : "it does not fit even in 4-bit"}, with llama.cpp and one request.`
        : `No. Even in 4-bit it needs ${plain(spark.counts[q4i])} DGX Spark units or ${plain(halo.counts[q4i])} Ryzen AI Max+ 395 machines (128 GB), linked over the network.`,
    },
    // Strata (moteur pour PC de jeu) : chiffres publiés par le projet, cités comme tels.
    ...(strata ? [{
      q: `Can ${name} run on a gaming PC?`,
      a: `Yes, with Strata, an open-source engine for gaming PCs. ${strataHow(strata)} ` +
        `It needs a GPU with ${strata.minVramGB} GB of VRAM or more, ${strata.memory === "combined" ? `at least ${Math.min(...strata.ram.map((r) => r.gb))} GB of RAM and VRAM together` : `${Math.min(...strata.ram.map((r) => r.gb))} GB of RAM or more`} and about ${strata.diskGB} GB of disk. ` +
        `The Strata project measured ${strataWrite(strataFastest(strata))} tokens/s with its ${strataFastest(strata).size} version on an ${strataFastest(strata).setup}.`,
    }] : []),
    {
      q: `What is the cheapest way to run ${name}?`,
      a: setups.length
        ? `On ${date}, the cheapest on-demand setup is ${setupText(setups[0])} (about ${usd(setups[0].hourly * HOURS_PER_MONTH)} per month).` +
          (setups[1] ? ` Next: ${setupText(setups[1])}.` : "") +
          ` Sized for one ${SHEET_CONTEXT / 1024}K-token request; prices are checked every hour.`
        : `It needs more than one 8-GPU server on every GPU we track. The LLM VRAM calculator sizes multi-server setups.`,
    },
    {
      q: `How many H100 GPUs does ${name} need?`,
      a: `With one request and an 8K-token context: ${plain(h100.counts[idx("bf16")])} in BF16, ${plain(h100.counts[idx("fp8")])} in FP8 and ${plain(h100.counts[q4i])} in 4-bit (vLLM, 90% of memory usable).` +
        ([idx("bf16"), idx("fp8"), q4i].some((i) => (h100.counts[i] ?? 99) > 8) ? " Above 8 GPUs, that means several servers." : "") +
        ` Serving many users at once needs more memory for their KV caches: the calculator sizes that for you.`,
    },
    ...(about && (about.inputs?.length ?? 1) > 1 ? [{
      q: `Can ${name} read images?`,
      a: about.inputs!.includes("image")
        ? `Yes. ${name} reads ${readsText(about)}. Images add tokens to the context, so they also add KV cache.`
        : `It reads ${readsText(about)}.`,
    }] : []),
    ...(about?.tools ? [{
      q: `Does ${name} support tool calling?`,
      a: `Yes, ${name} supports tool (function) calling: it can ask your application to run a function and use the result.`,
    }] : []),
    ...(about?.thinking ? [{
      q: `Does ${name} have a thinking mode?`,
      a: `${reasoningText(about)}.` + (about.effortVia ? ` Pick the level in ${about.effortVia}.` : "") +
        (about.switch ? ` With vLLM or SGLang, set it per request with "chat_template_kwargs": {"${about.switch}": false} (or true).` : "") +
        " Reasoning tokens count toward the context and the KV cache.",
    }] : []),
    ...(about?.sampling ? [{
      q: `What temperature should I use with ${name}?`,
      a: about.sampling.source === "openrouter"
        ? `The repository sets no defaults; OpenRouter uses ${samplingSummary(about.sampling)}.`
        : `${about.sampling.source === "card" ? "The official model card recommends" : "The official defaults are"} ${samplingSummary(about.sampling)}.`,
    }] : []),
    ...(about?.cutoff ? [{
      q: `What is ${name}'s knowledge cutoff?`,
      a: `${monthYear(about.cutoff)}, ${about.cutoffFrom === "card" ? "according to its official model card" : "according to OpenRouter"}: it knows little about later events.`,
    }] : []),
  ];
}

export default function VramModelPage() {
  const { slug = "" } = useParams();
  const { prices } = usePrices();
  // Catalogue de la veille : licence, popularité, Ollama, moteurs (instantané du build, puis l'heure).
  const catalog = useLiveData("/data/catalog.json", CATALOG_SNAPSHOT, validateCatalog) ?? CATALOG_SNAPSHOT;
  const model = modelBySlug(slug);
  if (!model) return <NotFoundPage />;

  const s = modelSheet(model);
  const lab = labOf(model, catalog);
  const entry = catalog.models[model.id];
  const license = entry && licenseInfo(entry.license, entry.licenseName);
  // Fine-tune : sa fiche renvoie à celle du modèle d'origine quand le site l'a.
  const parentModel = entry?.parent ? MODELS.find((m) => m.repo.toLowerCase() === entry.parent!.toLowerCase()) : undefined;
  const engines = entry?.engines;
  // « Runs with » : seulement ce que les registres officiels ont confirmé (lu chaque jour).
  const rw = runsWith(engines);
  const name = modelName(model);
  const path = modelPath(model);
  const date = prices.updated.slice(0, 10);
  const setups = cheapestSetups(model, s, prices);
  const about = entry?.about;
  const faq = faqFor(model, s, setups, date, entry?.strata, about);
  // Une phrase sur ce qu'il sait faire, sous le titre : seulement ce que ses sources disent.
  const abilityParts = about ? [
    (about.inputs?.length ?? 1) > 1 ? `reads ${readsText(about)}` : "",
    about.tools ? "calls tools" : "",
    about.thinking === "toggle" ? "has a thinking mode you can switch off" : about.thinking === "effort" ? "lets you set its reasoning effort"
      : about.thinking === "always" ? "reasons before it answers" : about.thinking === "supported" ? "has a reasoning mode" : "",
  ].filter(Boolean) : [];
  const abilities = abilityParts.length ? `It ${andList(abilityParts)}.` : "";
  const samplingFlags = about?.sampling ? samplingRows(about.sampling).map((r) => r[2]).join(" ") : "";
  const bf16 = s.formats.find((f) => f.id === "bf16")!;
  const q4 = s.formats.find((f) => f.id === "q4_k_m")!;
  const kv32 = s.kv.find((k) => k.ctx === 32768);
  const contexts = s.formats[0].withContextGiB.map((c) => c.ctx);
  const related = MODELS.filter((m) => m.group === model.group && m.id !== model.id);
  // InferenceX : la dernière mesure réelle sur chaque GPU (la configuration la plus petite), à côté de
  // notre estimation dans les mêmes conditions. Jamais à sa place : elle couvre quelques configurations.
  const ixRuns = (entry?.verified?.inferencex?.runs ?? [])
    .filter((x, i, all) => all.findIndex((y) => y.gpu === x.gpu) === i).slice(0, 4)
    .flatMap((x) => {
      const g = GPUS.find((y) => y.id === x.gpu), rt = RUNTIMES.find((r) => r.id === x.engine);
      const bits = WEIGHT_FORMATS.find((f) => f.id === x.format)?.bits;
      if (!g || !rt || bits === undefined) return [];
      const at = {
        model, mode: "inference" as const, weightBits: bits, kvBytes: 2, context: 1536, sequences: x.conc ?? 1, tp: x.gpus, pp: 1,
        vramGiB: usableMemGiB(g), utilization: x.utilization ?? rt.utilization, overheadGiB: rt.overheadGiB,
        target: { engine: x.engine, gpu: g, userUtilization: x.utilization !== undefined }, gradientCheckpointing: false, shardStates: false, loraFraction: 0,
      };
      const calc = `/llm-vram-calculator?model=${model.id}&gpu=${x.gpu}&engine=${x.engine}&n=${x.gpus}&w=${x.format}${x.conc ? `&req=${x.conc}` : ""}`;
      return [{ x, g, weights: weightsPerGpu(at, x.gpus, 1) / GiB, speed: decodeSpeed(at, g, rt.bwEfficiency)?.perRequest, calc }];
    });
  // Fichiers GGUF publiés, du plus gros au plus petit, et la plus petite carte où chacun tient.
  const ggufRows = Object.entries(entry?.ggufFiles?.files ?? {})
    .sort(([, a], [, b]) => b - a)
    .map(([quant, bytes]) => ({ quant, bytes, card: smallestCard(model, bytes) }));
  const ggufKey = ggufRows.filter((r, i) => GGUF_KEY.test(r.quant) || i === ggufRows.length - 1);
  const [ggufShown, ggufMore] = ggufRows.length > 10 && ggufKey.length >= 5
    ? [ggufKey, ggufRows.filter((r) => !ggufKey.includes(r))] : [ggufRows, []];
  const checks = entry?.verified && (entry.verified.gguf || entry.verified.gpu || ixRuns.length > 0);

  return (
    <>
      <SeoHead {...VRAM_MODEL_SEO[path]} />
      <Seo
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${SITE}${path}`,
              name: `${name} VRAM Requirements`,
              description: VRAM_MODEL_SEO[path].description,
              url: `${SITE}${path}`,
              inLanguage: "en",
              dateModified: VRAM_MODEL_SEO[path].lastmod,
              // Le modèle dont parle la page : son labo et sa date de sortie.
              about: {
                "@type": "CreativeWork",
                name,
                ...(lab ? { creator: { "@type": "Organization", name: lab.name } } : {}),
                ...(model.released ? { dateCreated: model.released } : {}),
                ...(model.repo ? { url: `https://huggingface.co/${model.repo}` } : {}),
              },
            },
            {
              "@type": "FAQPage",
              mainEntity: faq.map(({ q, a }) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "LLM VRAM Calculator", item: `${SITE}/llm-vram-calculator` },
                { "@type": "ListItem", position: 3, name: `${name} VRAM`, item: `${SITE}${path}` },
              ],
            },
          ],
        }}
      />
      <main id="main" className="vram-page">
        <section className="vram-hero">
          <nav className="vram-crumbs" aria-label="Breadcrumb">
            <Link to="/llm-vram-calculator">LLM VRAM Calculator</Link> <span aria-hidden="true">›</span> {name}
          </nav>
          <h1 className="vram-title vram-title-model">{name} VRAM Requirements</h1>
          {(model.released || lab) && (
            <p className="vram-released">
              {lab && <><Flag country={lab.country} />{lab.name}{lab.country ? `, ${countryName(lab.country)}` : ""}</>}
              {lab && model.released && " · "}
              {model.released && <>{model.preview ? "Announced" : "Released"} {longDate(model.released)}</>}
              {isNewModel(model, date) && <span className="vram-new">New</span>}
            </p>
          )}
          <p className="vram-subtitle">
            {name} has {model.params < 10 ? model.params.toFixed(2) : model.params.toFixed(1)}B parameters
            {model.activeParams ? ` (${model.activeParams}B active per token)` : ""}. In BF16 its weights alone take{" "}
            <strong>{gib(bf16.weightsGiB)} GiB</strong>; quantized to 4-bit, about <strong>{gib(q4.weightsGiB)} GiB</strong>.
            {!model.kv.length ? <> No KV cache: each request adds a fixed {gib(s.stateGiB)} GiB state, whatever the context.</>
              : kv32 ? <> Each 32K-token request adds {gib(kv32.bf16GiB)} GiB of KV cache.</> : null}
            {setups[0] ? <> Cheapest way to run it today: <strong>{setups[0].n} × {gpuWithMem(setups[0].g)}</strong> at {usd(setups[0].hourly)} per hour.</> : null}
            {abilities ? <> {abilities}</> : null}
          </p>
          {model.preview && <p className="vram-note vram-warn">{model.preview.note}</p>}
          <Link className="vram-cta" to={`/llm-vram-calculator?model=${model.id}`}>Size your own setup in the calculator →</Link>
        </section>

        <section className="vram-content">
          {/* Fiche d'identité : le preset (physique) et le catalogue de la veille (le reste). */}
          <article className="vram-content-card" id="facts">
            <h2>{name} at a glance</h2>
            <dl className="vram-facts">
              {lab && (
                <div><dt>Made by</dt><dd><Flag country={lab.country} />{lab.name}{lab.country ? `, ${countryName(lab.country)}` : ""}</dd></div>
              )}
              {entry?.parent && (
                <div>
                  <dt>Fine-tune of</dt>
                  <dd>
                    {parentModel ? <Link className="vram-link" to={modelPath(parentModel)}>{modelName(parentModel)}</Link> : (
                      <a className="vram-link" href={`https://huggingface.co/${entry.parent}`} target="_blank" rel="noopener noreferrer">
                        {entry.parent}<span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    )}
                  </dd>
                </div>
              )}
              {model.released && (
                <div><dt>{model.preview ? "Announced" : "Released"}</dt><dd>{longDate(model.released)}</dd></div>
              )}
              <div>
                <dt>Parameters</dt>
                <dd>{model.params < 10 ? model.params.toFixed(2) : model.params.toFixed(1)}B{model.activeParams ? `, ${model.activeParams}B active` : ""}</dd>
              </div>
              {Number.isFinite(model.maxContext) && (
                <div>
                  <dt>Context window</dt>
                  <dd>{tokens(model.maxContext)} tokens{model.yarn ? `, with YaRN (${tokens(model.yarn.original)} native)` : ""}</dd>
                </div>
              )}
              {license ? (
                <div><dt>License</dt><dd>{license.label}{license.permissive ? ", commercial use allowed" : ""}</dd></div>
              ) : model.repo && !model.preview && entry && (
                <div>
                  <dt>License</dt>
                  <dd>
                    Not declared in its Hugging Face metadata: see the{" "}
                    <a className="vram-link" href={`https://huggingface.co/${model.repo}`} target="_blank" rel="noopener noreferrer">model card<span className="sr-only"> (opens in a new tab)</span></a>
                  </dd>
                </div>
              )}
              {entry?.gated && model.repo && (
                <div>
                  <dt>Access</dt>
                  <dd>
                    Gated: accept the license on{" "}
                    <a className="vram-link" href={`https://huggingface.co/${model.repo}`} target="_blank" rel="noopener noreferrer">Hugging Face<span className="sr-only"> (opens in a new tab)</span></a>
                    {" "}before downloading the weights
                  </dd>
                </div>
              )}
              {/* Ce que le modèle sait faire : ses fichiers officiels, sa fiche, OpenRouter (modelcard.ts). */}
              {about && (
                <div><dt>Reads</dt><dd>{(about.inputs?.length ?? 1) > 1 ? capital(readsText(about)) : "Text only"}</dd></div>
              )}
              {about?.tools && (
                <div><dt>Tool calling</dt><dd>Supported</dd></div>
              )}
              {about && reasoningText(about) && (
                <div><dt>Reasoning</dt><dd>{reasoningText(about)}</dd></div>
              )}
              {about?.languages && about.languages.length > 1 && (
                <div>
                  <dt>Languages</dt>
                  <dd>
                    {about.languages.slice(0, 6).map(languageName).join(", ")}
                    {about.languages.length > 6 ? ` and ${about.languages.length - 6} more` : ""}
                  </dd>
                </div>
              )}
              {about?.cutoff && (
                <div>
                  <dt>Knowledge cutoff</dt>
                  <dd>{monthYear(about.cutoff)}<p className="vram-facts-note">{about.cutoffFrom === "card" ? "Official model card" : "OpenRouter"}</p></dd>
                </div>
              )}
              {about?.papers && (
                <div>
                  <dt>{about.papers.length > 1 ? "Papers" : "Paper"}</dt>
                  <dd>
                    {about.papers.map((p) => (
                      <p key={p.id} className="vram-facts-note vram-facts-paper">
                        <a className="vram-link" href={`https://arxiv.org/abs/${p.id}`} target="_blank" rel="noopener noreferrer">
                          {p.title}<span className="sr-only"> (opens in a new tab)</span>
                        </a>{" "}({p.date.slice(0, 4)})
                      </p>
                    ))}
                  </dd>
                </div>
              )}
              {about?.training && (
                <div>
                  <dt>Training</dt>
                  <dd>
                    {[
                      about.training.flop && <span key="f">{sci(about.training.flop).m} × 10<sup>{sci(about.training.flop).e}</sup> FLOP</span>,
                      about.training.tokens && <span key="t">{bigTokens(about.training.tokens)} tokens</span>,
                      about.training.hardware && <span key="h">{about.training.chips ? `${about.training.chips.toLocaleString("en-US")} × ` : ""}{about.training.hardware}</span>,
                    ].filter(Boolean).map((x, i) => <span key={i}>{i > 0 && " · "}{x}</span>)}
                    <p className="vram-facts-note">
                      <a className="vram-link" href="https://epoch.ai/data/ai-models" target="_blank" rel="noopener noreferrer">Epoch AI<span className="sr-only"> (opens in a new tab)</span></a>
                      {about.training.confidence && about.training.confidence !== "Confident" ? `, ${about.training.confidence.toLowerCase()} estimate` : ""}, CC BY 4.0
                    </p>
                  </dd>
                </div>
              )}
              {entry?.hf && model.repo && (entry.hf.likes > 0 || entry.hf.downloads > 0) && (
                <div>
                  <dt>On Hugging Face</dt>
                  <dd>
                    <a className="vram-link" href={`https://huggingface.co/${model.repo}`} target="_blank" rel="noopener noreferrer">
                      {compact(entry.hf.likes)} likes, {compact(entry.hf.downloads)} downloads last month<span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </dd>
                </div>
              )}
              {entry?.modelscope && (
                <div>
                  <dt>On ModelScope</dt>
                  <dd>
                    <a className="vram-link" href={`https://modelscope.cn/models/${entry.modelscope}`} target="_blank" rel="noopener noreferrer">
                      {entry.modelscope}<span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </dd>
                </div>
              )}
              {engines && rw && (
                <div>
                  <dt>Runs with</dt>
                  <dd>
                    {rw.names.length ? rw.names.join(", ") : "Not yet in vLLM or llama.cpp"}
                    {engines.recipe && (
                      <> · <a className="vram-link" href={engines.recipe} target="_blank" rel="noopener noreferrer">vLLM recipe<span className="sr-only"> (opens in a new tab)</span></a></>
                    )}
                    {/* Hors du registre de vLLM, mais documenté par la fiche du modèle (plugin du labo). */}
                    {rw.viaCard && (
                      <p className="vram-facts-note">
                        {rw.setup ? <>vLLM needs one extra install first: <code className="vram-facts-code">{rw.setup}</code></> : "vLLM needs the extra setup described on the model card"}
                        {" "}(<a className="vram-link" href={`https://huggingface.co/${model.repo}`} target="_blank" rel="noopener noreferrer">model card<span className="sr-only"> (opens in a new tab)</span></a>)
                      </p>
                    )}
                    {/* Hors du convertisseur de llama.cpp, mais une version GGUF existe : lisible ou pas par llama.cpp. */}
                    {rw.gguf && (
                      <p className="vram-facts-note">
                        {rw.gguf.runs ? "llama.cpp runs its GGUF build " : "GGUF builds exist, such as "}
                        <a className="vram-link" href={`https://huggingface.co/${rw.gguf.repo}`} target="_blank" rel="noopener noreferrer">{rw.gguf.repo}<span className="sr-only"> (opens in a new tab)</span></a>
                        {rw.gguf.runs ? "" : <>, but they need a patched llama.cpp: <code className="vram-facts-code">{rw.gguf.arch}</code> is not in mainline yet</>}
                      </p>
                    )}
                  </dd>
                </div>
              )}
              {checks && entry?.verified && (
                <div>
                  <dt>Checked against</dt>
                  <dd>
                    {entry.verified.gguf && (
                      <p className="vram-facts-note">
                        {entry.verified.gguf.kv === "match" ? "KV cache matches" : "KV cache differs from"} the GGUF header of{" "}
                        <a className="vram-link" href={`https://huggingface.co/${entry.verified.gguf.repo}`} target="_blank" rel="noopener noreferrer">
                          {entry.verified.gguf.repo}<span className="sr-only"> (opens in a new tab)</span>
                        </a>{" "}(what llama.cpp allocates), {longDate(entry.verified.gguf.day)}
                      </p>
                    )}
                    {entry.verified.gpu && (
                      <p className="vram-facts-note">
                        Measured on {GPUS.find((g) => g.id === entry.verified!.gpu!.gpu)?.label.split(" · ")[0] ?? entry.verified.gpu.gpu} with {entry.verified.gpu.engine}: {entry.verified.gpu.weightsGiB} GiB of weights
                        loaded, {entry.verified.gpu.oursGiB} GiB estimated ({longDate(entry.verified.gpu.day)})
                      </p>
                    )}
                    {ixRuns.length > 0 && (
                      <p className="vram-facts-note">
                        {ixRuns.length} real server run{ixRuns.length > 1 ? "s" : ""} (InferenceX):{" "}
                        <a className="vram-link" href="#real-runs">estimate vs measured</a>
                      </p>
                    )}
                  </dd>
                </div>
              )}
              {entry?.ollama && (
                <div>
                  <dt>On Ollama</dt>
                  <dd>
                    <code className="vram-facts-code">{ollamaCommand(entry.ollama)}</code>
                    {entry.ollama.bytes ? <> · {entry.ollama.quant ? `${entry.ollama.quant}, ` : ""}{(entry.ollama.bytes / GiB).toFixed(1)} GiB</> : null}
                    {" "}<a className="vram-link" href={`https://ollama.com/library/${entry.ollama.name}`} target="_blank" rel="noopener noreferrer">
                      {compact(entry.ollama.pulls)} pulls<span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </article>

          <article className="vram-content-card">
            <h2>VRAM by precision and context length</h2>
            <p>Weights plus the KV cache of one request (BF16 cache){s.stateGiB > 0 ? " and the model's fixed recurrent state" : ""}, in GiB. Add 1 to 3 GiB for the inference engine itself.</p>
            <div className="vram-table-wrap">
              <table className="vram-table">
                <thead>
                  <tr>
                    <th scope="col">Weights format</th>
                    <th scope="col">Weights only</th>
                    {contexts.map((c) => <th scope="col" key={c}>+ {tokens(c)} context</th>)}
                  </tr>
                </thead>
                <tbody>
                  {s.formats.map((f) => (
                    <tr key={f.id}>
                      <th scope="row">{f.label}</th>
                      <td>{gib(f.weightsGiB)}</td>
                      {f.withContextGiB.map((c) => <td key={c.ctx}>{gib(c.gib)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          {ggufRows.length > 0 && entry?.ggufFiles && (
            <article className="vram-content-card" id="gguf">
              <h2>{name} GGUF files</h2>
              <p>
                Exact size of each quantization in{" "}
                <a className="vram-link" href={`https://huggingface.co/${entry.ggufFiles.repo}`} target="_blank" rel="noopener noreferrer">
                  {entry.ggufFiles.repo}<span className="sr-only"> (opens in a new tab)</span>
                </a>, and the smallest GPU that runs it with llama.cpp, Ollama or LM Studio and an {tokens(SHEET_CONTEXT)}-token context.
              </p>
              <div className="vram-table-wrap">
                <table className="vram-table">
                  <thead><tr><th scope="col">Quantization</th><th scope="col">File size</th><th scope="col">Smallest GPU</th></tr></thead>
                  <tbody>{ggufShown.map(ggufRow)}</tbody>
                </table>
              </div>
              {ggufMore.length > 0 && (
                <details className="vram-more">
                  <summary className="vram-summary">Show the other {ggufMore.length} files</summary>
                  <div className="vram-table-wrap">
                    <table className="vram-table">
                      <thead><tr><th scope="col">Quantization</th><th scope="col">File size</th><th scope="col">Smallest GPU</th></tr></thead>
                      <tbody>{ggufMore.map(ggufRow)}</tbody>
                    </table>
                  </div>
                </details>
              )}
              <p className="vram-footnote">Read every day from Hugging Face. Split files are added together; vision projectors are left out.</p>
            </article>
          )}

          <article className="vram-content-card">
            <h2>How many GPUs to run {name}</h2>
            <p>
              Fewest GPUs that fit the weights plus one {tokens(SHEET_CONTEXT)}-token request. Data-center GPUs use vLLM (90% of memory usable),
              the others llama.cpp. Several GPUs split the model with tensor or pipeline parallelism.
            </p>
            <div className="vram-table-wrap">
              <table className="vram-table">
                <thead>
                  <tr>
                    <th scope="col">GPU</th>
                    {s.formats.map((f) => <th scope="col" key={f.id}>{f.id === "fp8" ? "8-bit" : f.id === "q4_k_m" ? "4-bit" : f.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {s.gpus.map((g) => (
                    <tr key={g.id}>
                      <th scope="row">{g.label} <span className="vram-table-dim">{g.vram} GB{g.label.includes(" GB") ? " usable" : ""}</span></th>
                      {g.counts.map((n, i) => g.supported[i]
                        ? <td key={i} className={n === 1 ? "vram-table-ok" : undefined}>{count(n, g.engine === "vLLM")}</td>
                        : <td key={i} className="vram-table-dim">Not on this GPU</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="vram-footnote">
              8-bit: FP8 with vLLM (INT8 on GPUs without FP8), Q8_0 with llama.cpp. 4-bit: AWQ / GPTQ with vLLM, Q4_K_M with
              llama.cpp, both counted at the Q4_K_M size.
              {s.gpus.some((g) => g.supported.includes(false)) && (
                <> Not on this GPU: the official {model.native!.label.replace(" (native)", "")} weights do not run there (llama.cpp needs a GGUF
                  version{model.native!.needs ? `, and vLLM needs a GPU with ${model.native!.needs.toUpperCase()} support` : ""}).</>
              )}
            </p>
          </article>

          {ixRuns.length > 0 && (
            <article className="vram-content-card" id="real-runs">
              <h2>{name} on real servers</h2>
              <p>
                Server starts published by{" "}
                <a className="vram-link" href="https://inferencex.semianalysis.com/inference" target="_blank" rel="noopener noreferrer">InferenceX<span className="sr-only"> (opens in a new tab)</span></a>{" "}
                (SemiAnalysis, Apache 2.0), 1K-token prompts and answers, next to our estimate for the same setup. They cover a few
                setups; our figures cover every one.
              </p>
              <div className="vram-table-wrap">
                <table className="vram-table">
                  <thead>
                    <tr><th scope="col">Setup</th><th scope="col">Weights per GPU</th><th scope="col">Speed per request</th><th scope="col">Measured</th></tr>
                  </thead>
                  <tbody>
                    {ixRuns.map(({ x, g, weights, speed, calc }) => (
                      <tr key={x.id}>
                        <th scope="row">
                          <Link className="vram-link" to={calc}>{x.gpus} × {gpuShort(g)}</Link>
                          {" "}<span className="vram-table-dim">{x.engine === "sglang" ? "SGLang" : "vLLM"}{x.version ? ` ${x.version}` : ""}</span>
                        </th>
                        <td>{x.weightsGiB ? <>{x.weightsGiB.toFixed(1)} GiB <span className="vram-table-dim">(estimate {weights.toFixed(1)})</span></> : "Not logged"}</td>
                        <td>
                          {x.tps ? <>{x.tps.toFixed(0)} tokens/s <span className="vram-table-dim">({x.conc} at once{speed ? `, estimate ${speed.toFixed(0)}` : ""})</span></> : "Not logged"}
                        </td>
                        <td>{longDate(x.day)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="vram-footnote">Each setup opens in the calculator with the same GPU, engine, format and number of requests.</p>
            </article>
          )}

          {entry?.strata && (
            <article className="vram-content-card" id="strata">
              <h2>Run {name} on a gaming PC with Strata</h2>
              <p>
                <a className="vram-link" href={`https://github.com/${entry.strata.repo}`} target="_blank" rel="noopener noreferrer">
                  Strata<span className="sr-only"> (opens in a new tab)</span>
                </a>{" "}
                is an open-source engine (MIT) for gaming PCs, by an independent developer{lab ? `, not by ${lab.name}` : ""}.{" "}
                {strataHow(entry.strata)} It needs a GPU with {entry.strata.minVramGB} GB of VRAM or more ({entry.strata.gpus}) and about {entry.strata.diskGB} GB
                of disk. {entry.strata.memory === "combined"
                  ? `Your RAM and VRAM together${entry.strata.reservedGB ? `, less ${entry.strata.reservedGB} GB kept by the system,` : ""} decide`
                  : "Your RAM decides"} which version fits:
              </p>
              <div className="vram-table-wrap">
                <table className="vram-table vram-table-words">
                  <thead><tr><th scope="col">{entry.strata.memory === "combined" ? "Your RAM + VRAM" : "Your RAM"}</th><th scope="col">Version to take</th></tr></thead>
                  <tbody>
                    {[...entry.strata.ram].sort((a, b) => a.gb - b.gb).map((r, i, all) => (
                      <tr key={r.gb}><th scope="row">{r.gb} GB{i === all.length - 1 ? " or more" : ""}</th><td>{r.pick}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="vram-table-wrap">
                <table className="vram-table vram-table-wraprow">
                  <thead>
                    <tr><th scope="col">PC</th><th scope="col">Version</th><th scope="col">Writes</th><th scope="col">Reads {aOrAn(tokens(entry.strata.promptTokens ?? 32768))} prompt</th></tr>
                  </thead>
                  <tbody>
                    {entry.strata.speeds.map((x) => (
                      <tr key={`${x.setup}|${x.size}`}>
                        <th scope="row">{x.setup}</th><td>{x.size}</td>
                        <td>{strataWrite(x)} tokens/s</td><td>{x.read.toLocaleString("en-US")} tokens/s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="vram-footnote">
                Requirements and speeds published by the Strata project (read on {entry.strata.checked}
                {entry.strata.version ? `; latest release ${entry.strata.version}` : ""}), not estimated here: the calculator sizes llama.cpp,
                whose RAM offload keeps whole layers of experts in RAM instead of the most used ones.
              </p>
            </article>
          )}

          <article className="vram-content-card" id="cheapest">
            <h2>Cheapest way to run {name} today</h2>
            <p>
              For each GPU: the fewest cards that fit {name} with one {SHEET_CONTEXT / 1024}K-token request, the most faithful
              weight format at that count, and the cheapest on-demand price ({date}).
            </p>
            {setups.length ? (
              <div className="vram-table-wrap">
                <table className="vram-table">
                  <thead>
                    <tr>
                      <th scope="col">Setup</th><th scope="col">Per hour</th><th scope="col">Weights</th>
                      <th scope="col">Provider</th><th scope="col">Per month</th><th scope="col"><span className="sr-only">Rent</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {setups.slice(0, 8).map((x, i) => (
                      <tr key={x.g.id}>
                        <th scope="row"><Link to={gpuPricePath(x.g)}>{x.n} × {gpuShort(x.g)}</Link>{!gpuShort(x.g).endsWith("GB") && <> <span className="vram-table-dim">{x.g.vram} GB</span></>}</th>
                        <td className={i === 0 ? "vram-table-ok" : undefined}>{usd(x.hourly)}</td>
                        <td>{x.label.replace(" (native)", " (official)")}</td>
                        <td>{x.offer.name.split(" (")[0]}</td>
                        <td>{usd(x.hourly * HOURS_PER_MONTH)}</td>
                        <td>
                          <a href={rentUrl(x.offer)} target="_blank" rel={rentRel(x.offer)}>
                            Rent<span className="sr-only"> {x.n} × {gpuShort(x.g)} on {x.offer.name.split(" (")[0]} (opens in a new tab)</span> →
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>{name} needs more than one 8-GPU server on every GPU we track: the <Link className="vram-link" to={`/llm-vram-calculator?model=${model.id}`}>calculator</Link> sizes multi-server setups.</p>
            )}
            <p className="vram-footnote">
              On-demand prices from {PRICE_SOURCE_LIST}, checked every hour. Serving many users needs more KV cache, so more memory:
              size it in the <Link className="vram-link" to={`/llm-vram-calculator?model=${model.id}`}>calculator</Link>. Some links are affiliate links:
              StudioTV may earn a commission, at no extra cost to you.
            </p>
            <SpareCoresCredit />
          </article>

          {entry?.apis && (
            <article className="vram-content-card" id="api-providers">
              <h2>Or use {name} through an API</h2>
              <p>
                No GPU to rent: these providers serve {name} and bill per token. Speeds are measured by Hugging Face for one request.
              </p>
              <div className="vram-table-wrap">
                <table className="vram-table">
                  <thead>
                    <tr>
                      <th scope="col">Provider</th><th scope="col">Output, per 1M tokens</th><th scope="col">Input, per 1M</th>
                      <th scope="col">Speed</th><th scope="col">First token</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.apis.providers.map((p, i) => (
                      <tr key={p.name}>
                        <th scope="row">{p.name}</th>
                        <td className={i === 0 ? "vram-table-ok" : undefined}>{usd(p.output)}</td>
                        <td>{usd(p.input)}</td>
                        <td>{p.tps ? `${p.tps.toLocaleString("en-US")} tokens/s` : ""}</td>
                        <td>{p.ttftMs ? (p.ttftMs < 1000 ? `${p.ttftMs} ms` : `${(p.ttftMs / 1000).toFixed(1)} s`) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="vram-footnote">
                Prices and speeds from{" "}
                <a className="vram-link" href="https://huggingface.co/docs/inference-providers" target="_blank" rel="noopener noreferrer">
                  Hugging Face Inference Providers<span className="sr-only"> (opens in a new tab)</span>
                </a>, read every day ({longDate(entry.apis.checked)}). An API wins at low volume; renting GPUs wins when they stay busy: the{" "}
                <Link className="vram-link" to={`/llm-vram-calculator?model=${model.id}`}>calculator</Link> gives the break-even.
              </p>
            </article>
          )}

          {about && (about.sampling || about.thinking) && (
            <article className="vram-content-card" id="settings">
              <h2>Recommended settings for {name}</h2>
              {about.sampling && (
                <>
                  <p>
                    {about.sampling.source === "generation_config" ? (
                      <>Sampling defaults from the official <a className="vram-link" href={`https://huggingface.co/${model.repo}/blob/main/generation_config.json`} target="_blank" rel="noopener noreferrer">generation_config.json<span className="sr-only"> (opens in a new tab)</span></a> of {model.repo}.</>
                    ) : about.sampling.source === "card" ? (
                      <>Recommended in the official <a className="vram-link" href={`https://huggingface.co/${model.repo}`} target="_blank" rel="noopener noreferrer">model card<span className="sr-only"> (opens in a new tab)</span></a>.</>
                    ) : (
                      <>The repository sets no sampling defaults: these are OpenRouter's defaults for {name}.</>
                    )}
                  </p>
                  <div className="vram-table-wrap">
                    <table className="vram-table">
                      <thead><tr><th scope="col">Setting</th><th scope="col">Value</th></tr></thead>
                      <tbody>{samplingRows(about.sampling).map(([k, v]) => <tr key={k}><th scope="row">{k}</th><td>{v}</td></tr>)}</tbody>
                    </table>
                  </div>
                  <p className="vram-footnote">
                    {about.sampling.source === "generation_config" ? "vLLM and SGLang apply these defaults unless a request sets its own. " : ""}
                    With llama.cpp, pass them yourself: <code className="vram-facts-code">{samplingFlags}</code>
                  </p>
                </>
              )}
              {about.thinking && (
                <p>
                  <strong>Reasoning.</strong> {reasoningText(about)}.
                  {about.switch ? <> With vLLM or SGLang, set it per request: <code className="vram-facts-code">{`"chat_template_kwargs": {"${about.switch}": false}`}</code> turns it off, <code className="vram-facts-code">true</code> on.</> : null}
                  {about.effortVia ? <> Pick the level in {about.effortVia}.</> : null}
                  {" "}Reasoning tokens take context and KV cache like any other token: leave room for them.
                </p>
              )}
            </article>
          )}

          <article className="vram-content-card">
            <h2>KV cache: how memory grows with context</h2>
            <p>
              Architecture: {model.arch}. {kvNote(model, s.kvSavingVsFullAttention)}
              {model.activeParams ? ` Only ${model.activeParams}B parameters are active per token, but all ${model.params.toFixed(0)}B must sit in memory: VRAM depends on the total, speed on the active part.` : ""}
            </p>
            <div className="vram-table-wrap">
              <table className="vram-table">
                <thead>
                  <tr><th scope="col">Context per request</th><th scope="col">BF16 cache</th><th scope="col">FP8 cache</th></tr>
                </thead>
                <tbody>
                  {s.kv.map((k) => (
                    <tr key={k.ctx}><th scope="row">{tokens(k.ctx)} tokens</th><td>{gib(k.bf16GiB)} GiB</td><td>{gib(k.fp8GiB)} GiB</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {s.stateGiB > 0 && <p>Plus {gib(s.stateGiB * 1024)} MiB of recurrent state per request, whatever the context.</p>}
          </article>

          <div className="vram-faq">
            <h2>{name} VRAM FAQ</h2>
            <div className="vram-faq-grid">
              {faq.map(({ q, a }) => (
                <div key={q} className="vram-faq-item">
                  <h3>{q}</h3>
                  <p>{a}</p>
                </div>
              ))}
            </div>
          </div>

          {!model.preview && (
            <article className="vram-content-card" id="badge">
              <h2>{name} VRAM badge</h2>
              <p>
                For a model card or a README: the 4-bit size and the smallest GPU it fits on, linked to this page. Data as JSON:{" "}
                <a className="vram-link" href={`/api/models/${modelSlug(model)}.json`}>/api/models/{modelSlug(model)}.json</a>.
              </p>
              <BadgeSnippet name={name} slug={modelSlug(model)} path={path} width={badgeWidth("VRAM", badgeValue(model))} />
            </article>
          )}

          <article className="vram-content-card">
            <h2>Other models</h2>
            {related.length > 0 && (
              <p>
                Same family ({model.group.toLowerCase()}):{" "}
                {related.map((m, i) => (
                  <span key={m.id}>{i > 0 && " · "}<Link className="vram-link" to={modelPath(m)}>{modelName(m)}</Link></span>
                ))}
              </p>
            )}
            <details className="vram-more">
              <summary className="vram-summary">All {MODELS.length - 1} other models</summary>
              <p>
                {MODELS.filter((m) => m.id !== model.id).map((m, i) => (
                  <span key={m.id}>{i > 0 && " · "}<Link className="vram-link" to={modelPath(m)}>{modelName(m)}</Link></span>
                ))}
              </p>
            </details>
            <p className="vram-footnote">
              Estimates, not guarantees: computed from the official config.json with the same engine as the{" "}
              <Link className="vram-link" to="/llm-vram-calculator">LLM VRAM Calculator</Link>. Real usage depends on your engine version and settings.
            </p>
          </article>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
